import React, { useMemo, useEffect } from "react";
import { useCommonStateFields } from "features/queryReports/hooks/useCommonStateFields";
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import classes from './Reports.module.scss';
import { disableSubmitButton, formatDate, underNpiLimit, validateFileName, validateNpis } from 'features/queryReports/utils/commonFunctions';
import QueryCreatedModal from 'features/queryReports/components/QueryCreatedModal';
import { useSubmitQuery } from 'features/queryReports/hooks/useSubmitQuery';
import { populationTrackerIDs } from 'features/queryReports/utils/reportIds';
import {
  CodeTypeSelect,
  DateRange,
  FileNameInput,
  MeasureTimeFrameSelect,
  NPIsInput,
  ProviderTypeSelect,
  VisualizationToggle,
  CheckboxInput
} from 'features/queryReports/components/FormFields';
import { applyCommonOutputs } from "features/queryReports/utils/queryCommon";
import populationTrackerOutputFields from "features/queryReports/utils/reportOutputFields/PopulationTrackerOutputFields";

function QueryLogicDescription({
  providerType,
  timeFrameDays,
  timeFrameDirection,
  npis,
  captureStartDate,
  captureEndDate
}) {
  return (
    <div><p>I would like to see <strong>{providerType}(s)</strong> who saw patients within <strong>{timeFrameDays} days {timeFrameDirection === '+' ? 'after' : 'prior'}</strong> {timeFrameDirection === '+' ? '' : 'to'} seeing <strong>{npis.split(',').join(', ')}</strong> between <strong>{formatDate(captureStartDate)}</strong> and <strong>{formatDate(captureEndDate)}</strong>.</p></div>
  )
}

function PopulationTracker({ bridge, payerMixValue, initialParams, showOpenMyQueries }) {
  const {
    categoryId,
    orgReportId,
    orgSourceReportId,
    physReportId,
    physSourceReportId
  } = populationTrackerIDs;
  const {
    state: common,
    bind,
    set,
    setOutput,
    resetCommon,
    hydrateFrom,
    selectors,
  } = useCommonStateFields({
    overrides: { payerMix: payerMixValue ?? "" },
  });

  const {
    submitQuery,
    waiting,
    showModal,
    modalData,
    closeModal,
  } = useSubmitQuery({ bridge, categoryId, classes, fileName: common.fileName });

  const confirmQuerySuccess = type => {
    if (type === 1) {
      closeModal();
      bridge.onMyQueryOpen();
    } else if (type === 2) {
      closeModal();
      bridge.onQuerySubmitted();
    }
  };

  const applyIncomingParams = data => {
    if (
      data.reportId === physReportId ||
      data.reportId === physSourceReportId
    ) {
      set("providerType", "physician");
    } else if (
      data.reportId === orgReportId ||
      data.reportId === orgSourceReportId
    ) {
      set("providerType", "organization");
    }
  }

  useEffect(() => {
    if (initialParams) {
      hydrateFrom(initialParams);
      applyIncomingParams(initialParams); // local compute set at initial data
      // any page-only hydration here as needed
    }
  }, [initialParams]);

  const queryObj = useMemo(() => {
    const retObj = {
      description: common.fileName,
      parameters: {
        providerlist: common.npis
          .split(',')
          .map((subStr) => subStr.replace(/\D/g, ''))
          .filter((item) => item !== ''),
        start_date: formatDate(common.startDate),
        end_date: formatDate(common.endDate),
        days: `${common.timeFrameDirection === '+' ? '' : '-'}${common.timeFrameDays}`,
        codeoutput: common.codeType,
        canBeVisualized: common.canBeVisualized,
      },
      outputFields: [
        common.outputs.outputCode && common.codeType === 'diag_code' ? 'ICD-10-CM' : null,
        common.outputs.outputCode && common.codeType === 'procedurecode' ? 'CPT/HCPCS Code' : null,
      ].filter((item) => item),
    };

    // common outputs
    applyCommonOutputs(retObj, common.outputs, {
      allowList: [
        'sourceNpi',
        'sourceName',
        'name',
        'address',
        'city',
        'stateName',
        'zipCode',
        'distinctClaimsCount',
        'distinctPatientsCount',
        'sumCharges',
        'placeOfService',
        'codeServiceLine',
        'codeDescription'
      ]
    });

    if (common.providerType === 'physician') {
      if (!common.outputs.sourceNpi && !common.outputs.sourceName) {
        retObj.reportId = physReportId;
      } else if (common.outputs.sourceNpi || common.outputs.sourceName) {
        retObj.reportId = physSourceReportId;
      }

      if (common.outputs.npi) retObj.outputFields.push('Rendering NPI');
      if (common.outputs.classification) retObj.outputFields.push('Physician Classification');
      if (common.outputs.specialization) retObj.outputFields.push('Physician Specialization');
      if (common.outputs.locationNpi) retObj.outputFields.push('Location NPI');
      if (common.outputs.locationName) retObj.outputFields.push('Location Name');
      if (common.outputs.locationClassification) retObj.outputFields.push('Location Classification');
      if (common.outputs.locationSpecialization) retObj.outputFields.push('Location Specialization');
      if (common.outputs.billingNpi) retObj.outputFields.push('Billing NPI');
      if (common.outputs.billingName) retObj.outputFields.push('Billing Name');
      if (common.outputs.billingClassification) retObj.outputFields.push('Billing Classification');
      if (common.outputs.billingSpecialization) retObj.outputFields.push('Billing Specialization');
      if (common.outputs.hospitalAffiliationIds) retObj.outputFields.push('Hospital Affiliation IDs');
      if (common.outputs.hospitalAffiliationNames) retObj.outputFields.push('Hospital Affiliation Names');
      if (common.outputs.groupPracticeIds) retObj.outputFields.push('Group Practice IDs');
      if (common.outputs.groupPracticeNames) retObj.outputFields.push('Group Practice Names');
    } else if (common.providerType === 'organization') {
      if (!common.outputs.sourceNpi && !common.outputs.sourceName) {
        retObj.reportId = orgReportId;
      } else if (common.outputs.sourceNpi || common.outputs.sourceName) {
        retObj.reportId = orgSourceReportId;
      }

      if (common.outputs.npi) retObj.outputFields.push('Organization');
      if (common.outputs.classification) retObj.outputFields.push('Organization Classification');
      if (common.outputs.specialization) retObj.outputFields.push('Organization Specialization');
    }

    if (common.canBeVisualized) {
      retObj.outputFields = [
        'Name',
        'Address',
        'City',
        'State',
        'ZIP Code',
        'Count of Distinct Claims',
        'Count of Distinct Patients',
        'Sum of Charges',
        'Place of Service',
        'Code Service Line',
        'Code Description',
      ];

      if (common.outputs.sourceNpi || common.outputs.sourceName) {
        retObj.outputFields = retObj.outputFields.concat(['Source NPI', 'Source Name']);
      }

      if (common.outputs.outputCode) {
        if (common.codeType === 'diag_code') {
          retObj.outputFields.push('ICD-10-CM');
        }
        if (common.codeType === 'procedurecode') {
          retObj.outputFields.push('CPT/HCPCS Code');
        }
      }

      if (common.providerType === 'physician') {
        retObj.outputFields = retObj.outputFields.concat([
          'Rendering NPI',
          'Physician Classification',
          'Physician Specialization',
          'Location NPI',
          'Location Name',
          'Location Classification',
          'Location Specialization',
          'Billing NPI',
          'Billing Name',
          'Billing Classification',
          'Billing Specialization',
          'Hospital Affiliation IDs',
          'Hospital Affiliation Names',
          'Group Practice IDs',
          'Group Practice Names',
        ]);
      }

      if (common.providerType === 'organization') {
        retObj.outputFields = retObj.outputFields.concat([
          'Organization',
          'Organization Classification',
          'Organization Specialization',
        ]);
      }
    }

    return retObj;
  }, [
    common.fileName,
    common.providerType,
    common.npis,
    common.startDate,
    common.endDate,
    common.timeFrameDirection,
    common.timeFrameDays,
    common.outputs,
    common.codeType,
    common.canBeVisualized
  ]);

  function clearFields() {
    if (!showOpenMyQueries) {
      bridge?.onClickCancelQueryReport();
      return;
    }
    resetCommon(); // clears shared fields
    // clear page-only here
  }

  return (
    <div className="container-fluid p-3">
      <div className={classes['report-content-wrap']}>
        <FileNameInput
          fileName={common.fileName}
          setFileName={(v) => set("fileName", v)}
          priorQueryParams={common.priorQueryParams}
        />
        <p className="text-center">Patient Capture Inputs</p>
        <NPIsInput bind={bind} common={common} />
        <DateRange common={common} bind={bind} label="Patient Capture Date" overlayText="Select Patients who saw the selected physicians between these dates" />
        <hr />
        <MeasureTimeFrameSelect bind={bind} categoryId={categoryId} />
        <ProviderTypeSelect common={common} bind={bind} label="Measure Provider Type" overlayText="Group the patient&apos;s clinical activity by provider or organization" />
        <CodeTypeSelect common={common} bind={bind} categoryId={categoryId} />
        <hr />
        <VisualizationToggle common={common} bind={bind} />

        {common.npis && validateNpis(common.npis) && common.timeFrameDirection && common.timeFrameDays && common.providerType && common.codeType && (
          <div className="row mb-3">
            <div className={classes['querylogic-wrapper']}>
              <FontAwesomeIcon icon={faCircleInfo} className={classes['info-icon']} />
              <div>
                <div className={classes['header']}>Query Logic</div>
                <div className={classes['body']}>
                  <QueryLogicDescription
                    providerType={common.providerType}
                    timeFrameDays={common.timeFrameDays}
                    timeFrameDirection={common.timeFrameDirection}
                    npis={common.npis}
                    captureStartDate={common.startDate}
                    captureEndDate={common.endDate}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {common.providerType && common.codeType && (
          <div className="row mb-3">
            <div className={classes['querylogic-wrapper']}>
              <FontAwesomeIcon icon={faCircleInfo} className={classes['info-icon']} />
              <div>
                <div className={classes['header']}>Output Fields</div>
                <div className={classes['body']}>
                  <div className="form-check form-check-inline">
                    <input
                      name="sourcenpi"
                      type="checkbox"
                      className="form-check-input"
                      checked={common.outputs.sourceNpi || (common.canBeVisualized && (common.outputs.sourceNpi || common.outputs.sourceName))}
                      onChange={(e) => {
                        if (common.canBeVisualized) {
                          setOutput("sourceName", e.target.checked)
                        }
                        setOutput("sourceNpi", e.target.checked)
                      }}
                    />
                    <label className="form-check-label">Source NPI*</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      name="sourcename"
                      type="checkbox"
                      className="form-check-input"
                      checked={common.outputs.sourceName || (common.canBeVisualized && (common.outputs.sourceNpi || common.outputs.sourceName))}
                      onChange={(e) => {
                        if (common.canBeVisualized) {
                          setOutput("sourceNpi", e.target.checked);
                        }
                        setOutput("sourceName", e.target.checked);
                      }}
                    />
                    <label className="form-check-label">Source Name*</label>
                  </div>
                  <p className="text-muted" style={{ fontSize: '.8rem' }}>
                    * Adding source provider will inflate patient counts if the patient saw
                    two or more of the source physicians.
                  </p>
                  <hr />
                  {populationTrackerOutputFields.map((item, index) => (
                    <CheckboxInput
                      key={item.name || index}
                      item={item}
                      common={common}
                      selectors={selectors}
                      setOutput={setOutput}
                      />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {common.fileName &&
          common.npis &&
          validateNpis(common.npis) &&
          common.timeFrameDirection &&
          common.timeFrameDays &&
          common.providerType &&
          underNpiLimit(common.npis) &&
          common.codeType &&
          common.startDate &&
          common.endDate && (
            <div className={`row ${classes['submit-btn-wrap']}`}>
              <button
                type="button"
                className={`btn btn-primary mr-2 ${classes['submit-btn']}`}
                disabled={!validateFileName(common.fileName) || disableSubmitButton(common)}
                onClick={() => submitQuery(queryObj)}
              >
                Submit Query
                {waiting && (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                )}
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${classes['cancel-btn']}`}
                onClick={() => clearFields()}
              >
                Cancel
              </button>
            </div>
          )}

        <QueryCreatedModal
          show={showModal}
          header={modalData.header}
          body={modalData.body}
          classes={classes}
          onClose={() => confirmQuerySuccess(2)}
          onOpenMyQueries={() => confirmQuerySuccess(1)}
          showOpenMyQueries={showOpenMyQueries}
        />

      </div>
    </div>
  );
}

export default PopulationTracker;